// Audit log utilities for BuildMyShifts
// Tracks all changes to published rotas and AI recommendation usage

import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';

export const AUDIT_ACTIONS = {
  ROTA_CREATED: 'rota_created',
  ROTA_PUBLISHED: 'rota_published',
  ROTA_UPDATED: 'rota_updated',
  ROTA_UNPUBLISHED: 'rota_unpublished',
  SHIFT_CREATED: 'shift_created',
  SHIFT_ASSIGNED: 'shift_assigned',
  SHIFT_UPDATED: 'shift_updated',
  SHIFT_CANCELLED: 'shift_cancelled',
  SHIFT_DELETED: 'shift_deleted',
  SWAP_REQUESTED: 'swap_requested',
  SWAP_APPROVED: 'swap_approved',
  SWAP_REJECTED: 'swap_rejected',
  LEAVE_REQUESTED: 'leave_requested',
  LEAVE_APPROVED: 'leave_approved',
  LEAVE_REJECTED: 'leave_rejected',
  AI_SUGGESTION_ACCEPTED: 'ai_suggestion_accepted',
  AI_SUGGESTION_REJECTED: 'ai_suggestion_rejected',
  AI_AUTO_ASSIGN: 'ai_auto_assign',
  STAFF_CONFIRMED: 'staff_confirmed',
  STAFF_DECLINED: 'staff_declined',
  SETTINGS_UPDATED: 'settings_updated',
  // ── New: Shift block & template actions ──
  SHIFT_BLOCK_CREATED: 'shift_block_created',
  SHIFT_BLOCK_UPDATED: 'shift_block_updated',
  SHIFT_BLOCK_DELETED: 'shift_block_deleted',
  TEMPLATE_CREATED: 'template_created',
  TEMPLATE_UPDATED: 'template_updated',
  TEMPLATE_DELETED: 'template_deleted',
  TEMPLATE_APPLIED: 'template_applied',
  // ── New: Compliance & override ──
  COMPLIANCE_OVERRIDE: 'compliance_override',
  COMPLIANCE_WARNING_ACKNOWLEDGED: 'compliance_warning_acknowledged',
  FATIGUE_WARNING_ACKNOWLEDGED: 'fatigue_warning_acknowledged',
  FATIGUE_OVERRIDE: 'fatigue_override',
  // ── New: Role hierarchy ──
  ROLE_SUBSTITUTION: 'role_substitution',
  ROLE_HIERARCHY_UPDATED: 'role_hierarchy_updated',
  // ── New: Pay rules ──
  PAY_RULES_UPDATED: 'pay_rules_updated',
  SECTOR_CONFIG_UPDATED: 'sector_config_updated',
};

/**
 * Write an audit log entry
 */
export async function logAuditEvent(companyId, action, data = {}) {
  try {
    await addDoc(collection(db, 'companies', companyId, 'shiftsAuditLog'), {
      action,
      performedBy: data.performedBy || null,
      performedByName: data.performedByName || null,
      targetId: data.targetId || null,
      targetType: data.targetType || null, // 'shift' | 'rota' | 'swap' | 'leave'
      previousValue: data.previousValue || null,
      newValue: data.newValue || null,
      metadata: data.metadata || {},
      aiRelated: data.aiRelated || false,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error writing audit log:', error);
  }
}

/**
 * Log an AI recommendation outcome
 */
export async function logAIOutcome(companyId, data) {
  return logAuditEvent(companyId, data.accepted ? AUDIT_ACTIONS.AI_SUGGESTION_ACCEPTED : AUDIT_ACTIONS.AI_SUGGESTION_REJECTED, {
    performedBy: data.userId,
    performedByName: data.userName,
    targetId: data.shiftId,
    targetType: 'shift',
    metadata: {
      aiRecommendation: data.recommendation,
      confidence: data.confidence,
      reasonList: data.reasonList,
      selectedStaffId: data.selectedStaffId,
      suggestedStaffId: data.suggestedStaffId,
    },
    aiRelated: true,
  });
}

/**
 * Log a compliance override with reason
 */
export async function logComplianceOverride(companyId, data) {
  return logAuditEvent(companyId, AUDIT_ACTIONS.COMPLIANCE_OVERRIDE, {
    performedBy: data.userId,
    performedByName: data.userName,
    targetId: data.shiftId || data.rotaId,
    targetType: data.shiftId ? 'shift' : 'rota',
    metadata: {
      reason: data.reason,
      overriddenErrors: data.overriddenErrors,
      overriddenWarnings: data.overriddenWarnings,
      staffId: data.staffId,
    },
  });
}

/**
 * Log a fatigue warning acknowledgement
 */
export async function logFatigueAcknowledgement(companyId, data) {
  return logAuditEvent(companyId, AUDIT_ACTIONS.FATIGUE_WARNING_ACKNOWLEDGED, {
    performedBy: data.userId,
    performedByName: data.userName,
    targetId: data.staffId,
    targetType: 'staff',
    metadata: {
      fatigueScore: data.fatigueScore,
      fatigueLevel: data.fatigueLevel,
      reason: data.reason,
      affectedShiftIds: data.affectedShiftIds,
    },
  });
}

/**
 * Log a template lifecycle event
 */
export async function logTemplateEvent(companyId, action, data) {
  return logAuditEvent(companyId, action, {
    performedBy: data.userId,
    performedByName: data.userName,
    targetId: data.templateId,
    targetType: 'template',
    metadata: {
      templateName: data.templateName,
      sector: data.sector,
      segmentCount: data.segmentCount,
    },
  });
}

const auditLog = {
  AUDIT_ACTIONS,
  logAuditEvent,
  logAIOutcome,
  logComplianceOverride,
  logFatigueAcknowledgement,
  logTemplateEvent,
};
export default auditLog;
